import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  Euro,
  Loader2,
  TrendingUp,
  Repeat,
  CreditCard,
  Users,
  CheckCircle2,
  Clock,
  FileSpreadsheet
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const money = (value) => `€${Number(value || 0).toFixed(2).replace('.', ',')}`;

const monthLabel = (key) => {
  const [year, month] = (key || '').split('-');
  return `${MONTHS_PT[Number(month) - 1] || key} ${String(year).slice(2)}`;
};

const PLAN_STYLES = {
  free: 'text-gray-400',
  pro: 'text-[#32CD32]',
  premium: 'text-yellow-400'
};

const MetricCard = ({ label, value, hint, icon: Icon, color, testId }) => (
  <Card className="bg-[#121212] border-white/10">
    <CardContent className="pt-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-gray-500">{label}</p>
          <p className={`text-2xl sm:text-3xl font-bold ${color}`} data-testid={testId}>{value}</p>
          {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
        </div>
        <div className="w-12 h-12 rounded-full bg-white/5 hidden sm:flex items-center justify-center flex-shrink-0">
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/20 rounded-lg p-3 shadow-xl">
      <p className="text-gray-400 text-sm mb-1">{label}</p>
      <p className="text-sm text-[#32CD32]">{money(payload[0].value)}</p>
    </div>
  );
};

export const RevenueTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('cardioai_token');
      const res = await axios.get(`${API}/admin/revenue/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `processbody_receita_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Ficheiro Excel descarregado');
    } catch (error) {
      toast.error('Erro ao exportar receita');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('cardioai_token');
    axios.get(`${API}/admin/revenue`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => toast.error('Erro ao carregar dados de receita'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20" data-testid="revenue-loading">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  if (!data) return null;

  const chartData = (data.monthly || []).map((m) => ({ ...m, label: monthLabel(m.month) }));
  const hasRevenue = chartData.some((m) => m.revenue > 0);

  return (
    <div className="space-y-6" data-testid="revenue-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-gray-400">
          Pagamentos, receita recorrente e distribuição de planos
        </p>
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="border-white/20 hover:border-[#32CD32] bg-transparent text-white hover:text-[#32CD32] w-full sm:w-auto"
          data-testid="export-revenue-btn"
        >
          {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
          Exportar Excel
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard
          label="Faturado (total)"
          value={money(data.total_revenue)}
          hint={`${data.payments_completed} pagamento(s)`}
          icon={Euro}
          color="text-[#32CD32]"
          testId="revenue-total"
        />
        <MetricCard
          label="Este Mês"
          value={money(data.month_revenue)}
          hint="pagamentos confirmados"
          icon={TrendingUp}
          color="text-white"
          testId="revenue-month"
        />
        <MetricCard
          label="Receita Recorrente"
          value={money(data.mrr)}
          hint="MRR dos planos ativos"
          icon={Repeat}
          color="text-yellow-400"
          testId="revenue-mrr"
        />
        <MetricCard
          label="Taxa de Conversão"
          value={`${data.conversion_rate}%`}
          hint={`${data.paying_users} de ${data.total_users} utilizadores`}
          icon={Users}
          color="text-blue-400"
          testId="revenue-conversion"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="bg-[#121212] border-white/10 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Receita por Mês</CardTitle>
            <CardDescription>Últimos 6 meses de pagamentos confirmados</CardDescription>
          </CardHeader>
          <CardContent>
            {hasRevenue ? (
              <div className="h-64" data-testid="revenue-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="label" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                    <YAxis stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                    <Bar dataKey="revenue" name="Receita" fill="#32CD32" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-center text-gray-500 gap-2" data-testid="revenue-chart-empty">
                <Euro className="w-10 h-10 text-gray-700" />
                <p className="text-sm">Ainda sem pagamentos confirmados.</p>
                <p className="text-xs">O gráfico enche-se automaticamente ao primeiro upgrade.</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">Distribuição de Planos</CardTitle>
            <CardDescription>Utilizadores e receita por plano</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.users_by_plan || {}).map(([planId, count]) => {
              const total = data.total_users || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={planId} data-testid={`plan-distribution-${planId}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className={`font-medium ${PLAN_STYLES[planId] || 'text-white'}`}>
                      {data.plan_names?.[planId] || planId} · {money(data.plan_prices?.[planId])}
                    </span>
                    <span className="text-gray-400">{count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-black/40 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${planId === 'premium' ? 'bg-yellow-400' : planId === 'pro' ? 'bg-[#32CD32]' : 'bg-gray-600'}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">
                    Faturado: {money(data.revenue_by_plan?.[planId])}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Pagamentos Recentes</CardTitle>
          <CardDescription>
            {data.payments_completed} confirmado(s) · {data.payments_pending} não concluído(s) ·
            {' '}valor médio {money(data.average_payment)}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(data.recent || []).length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center" data-testid="no-payments">
              Ainda não houve tentativas de pagamento.
            </p>
          ) : (
            <div className="space-y-2">
              {data.recent.map((p) => {
                const paid = p.status === 'completed';
                return (
                  <div
                    key={p.session_id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 px-3 bg-black/20 border border-white/5 rounded-lg"
                    data-testid={`payment-row-${p.session_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{p.email || p.name || 'Utilizador removido'}</p>
                      <p className="text-xs text-gray-500">
                        {p.plan_name} · {p.date ? new Date(p.date).toLocaleString('pt-PT') : '-'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`text-sm font-bold ${paid ? 'text-[#32CD32]' : 'text-gray-500'}`}>
                        {money(p.amount)}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full w-fit flex items-center gap-1 ${
                        paid ? 'bg-[#32CD32]/20 text-[#32CD32]' : 'bg-white/5 text-gray-400'
                      }`}>
                        {paid ? <CheckCircle2 className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {paid ? 'Pago' : 'Não concluído'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500 flex items-center gap-2">
        <CreditCard className="w-3 h-3" />
        Os valores são os cobrados em cada pagamento. Reembolsos feitos no Stripe não são refletidos aqui.
      </p>
    </div>
  );
};

export default RevenueTab;
